import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RETRY_LEAVES_LIVE_LINE, useChargeErrorCopy } from '../lib/useChargeErrorCopy';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';

import { MobileView } from '@/components';
import { CcAuthModal } from '@/components/CcAuthModal';
import { GuestyPayCardModal } from '@/components/GuestyPayCardModal';
import { PortalOutletContext } from '@/components/portal/PortalShellContainer';
import { Button } from '@/components/ui';

import { apiClient, formatCurrency } from '@/lib';
import { LanguageSelector } from '../components/LanguageSelector';
import { useLanguage } from '../contexts/LanguageContext';

// Terms Modal Component
interface TermsModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  content: string;
}

function TermsModal({ isOpen, onClose, title, content }: TermsModalProps) {
  const { t } = useTranslation('damageWaiver');
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-2xl max-w-lg w-full mx-4 max-h-[80vh] flex flex-col pointer-events-auto border border-gray-200">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="prose prose-sm max-w-none text-gray-700" dangerouslySetInnerHTML={{ __html: content }} />
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200">
          <Button onClick={onClose} variant="outline">
            {t('Close')}
          </Button>
        </div>
      </div>
    </div>
  );
}

interface DamageWaiverData {
  waiverCost: number;
  holdAmount: number | null; // null if user has selected no hold
  currency: string;
  configId: number;
  holdLengthDays: number;
  arrivalDate: string;
  departureDate: string;
  listingName: string;
  terms: {
    holds: string | null;
    waivers: string;
  };
  isCompleted?: boolean;
  defaultLanguage?: string;
  paymentProvider?: 'stripe' | 'guesty_pay';
  paymentProviderId?: string | null;
  ccAuthRequired?: boolean;
  // BES-7708: Guesty's estimated tax on the waiver fee, present only for GuestyPay
  // hosts (their waiver charge is waiverCost + this tax). Absent for everyone else.
  waiverTax?: number;
  waiverTaxRate?: number; // percent
}

interface CompletedWaiverData {
  isCompleted: true;
  amount: number | null;
  currency: string | null;
  // Null when completed via inbox mark-complete or backfill (BES-8925): only the
  // signed event exists, so the option taken outside Besty is unknown.
  type: 'waiver' | 'hold' | 'none' | null;
}

interface DamageWaiverRequest {
  option: 'hold' | 'waiver' | 'none';
  amount: number | null; // Null for none.
  currency: string;
  configId: number;
  returnPath?: string | null;
}

interface CheckoutResponse {
  checkoutUrl?: string;
  completed?: boolean;
  requiresCardInput?: boolean;
  paymentProviderId?: string | null;
  listingId?: string | null;
  pmsReservationId?: string | null;
  accountId?: string | null;
}

type ProtectionOption = 'waiver' | 'hold' | 'none';

function getSafeReturnPath(value: string | null): string | null {
  if (!value || !value.startsWith('/') || value.startsWith('//')) return null;
  return value;
}

interface ProtectionOptionCardProps {
  value: ProtectionOption;
  selected: string; // currently selected value from parent
  onChange: (value: string) => void;
  title: string;
  price?: string; // e.g. "15 USD"
  priceCaption?: string; // e.g. "one‑time fee"
  bullets?: string[];
  tag?: string; // e.g. "Most Popular"
  termsContent?: string; // HTML content for terms
  onShowTerms?: () => void; // callback to show terms modal
  icon?: React.ReactNode;
  disabled?: boolean;
  // BES-7708: optional cost breakdown (subtotal + "Estimated tax" + inclusive
  // total), revealed as a box when the card is selected, for GuestyPay hosts.
  // Labels/amounts are pre-translated and pre-formatted by the caller.
  taxBreakdown?: {
    subtotalLabel: string;
    subtotalAmount: string;
    taxLabel: string;
    taxAmount: string;
    totalLabel: string;
    totalAmount: string;
  };
}

function ProtectionOptionCard({
  value,
  selected,
  onChange,
  title,
  price,
  priceCaption,
  bullets = [],
  tag,
  termsContent,
  onShowTerms,
  icon,
  disabled = false,
  taxBreakdown,
}: ProtectionOptionCardProps) {
  const { t } = useTranslation('damageWaiver');
  const internalId = useId();
  const isSelected = selected === value;
  const [isHovered, setIsHovered] = useState(false);

  const colorMap = {
    waiver: {
      primary: 'rgb(62, 104, 192)',
      light: 'rgb(239, 246, 255)',
      hover: 'rgba(62, 104, 192, 0.1)',
    },
    hold: {
      primary: 'rgb(6, 78, 59)',
      light: 'rgb(240, 253, 250)',
      hover: 'rgba(6, 78, 59, 0.1)',
    },
    none: {
      primary: 'rgb(217, 119, 6)',
      light: 'rgb(255, 251, 235)',
      hover: 'rgba(217, 119, 6, 0.1)',
    },
  };

  const colors = colorMap[value] || colorMap.none;

  return (
    <div
      role="radio"
      aria-checked={isSelected}
      aria-labelledby={internalId}
      tabIndex={disabled ? -1 : 0}
      onClick={() => {
        !disabled && onChange(value);
      }}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onChange(value);
        }
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={[
        'group relative w-full rounded-2xl border p-5 outline-none transition-all',
        'border-zinc-200 shadow-sm hover:shadow-md',
        isSelected ? 'ring-2' : 'ring-1 ring-transparent',
        disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer',
        'focus-visible:ring-4',
      ].join(' ')}
      style={
        {
          backgroundColor: isSelected ? colors.light : isHovered ? colors.hover : 'white',
          borderColor: isSelected ? colors.primary : undefined,
          '--tw-ring-color': isSelected ? `${colors.primary}60` : 'transparent',
        } as React.CSSProperties
      }
    >
      {tag && (
        <span
          className="absolute right-4 top-4 inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium text-white shadow-sm"
          style={{ backgroundColor: colors.primary }}
        >
          {tag}
        </span>
      )}

      <div className="flex items-start gap-3">
        {/* Custom radio */}
        <span
          aria-hidden
          className="mt-1 grid h-5 w-5 place-items-center rounded-full border transition-colors"
          style={{
            backgroundColor: isSelected ? colors.primary : 'white',
            borderColor: isSelected ? 'transparent' : '#d1d5db',
          }}
        >
          <span
            className={`h-2.5 w-2.5 rounded-full bg-white transition-opacity ${isSelected ? 'opacity-100' : 'opacity-0'}`}
          />
        </span>

        <div className="flex-1">
          <div className="flex items-center gap-2">
            {icon && <span className="text-zinc-500">{icon}</span>}
            <h3 id={internalId} className="text-base font-semibold text-zinc-900">
              {title}
            </h3>
          </div>

          {/* Price */}
          {price && (
            <div className="mt-0 flex flex-col leading-none">
              <span className="text-xl font-semibold leading-none" style={{ color: colors.primary }}>
                {price}
              </span>
              {priceCaption && <span className="mt-0.5 text-sm leading-tight text-zinc-500">{priceCaption}</span>}
            </div>
          )}

          {/* Bullets */}
          {bullets.length > 0 && (
            <ul className="mt-3 space-y-2">
              {bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-zinc-700">
                  <svg
                    aria-hidden
                    className="mt-0.5 h-4 w-4"
                    style={{ color: colors.primary }}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.704 5.29a1 1 0 010 1.415l-7.2 7.2a1 1 0 01-1.415 0l-3-3a1 1 0 111.415-1.414l2.292 2.292 6.493-6.493a1 1 0 011.415 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}

          {/* Terms */}
          {termsContent && onShowTerms && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onShowTerms();
              }}
              className="mt-3 inline-flex items-center text-sm font-medium text-indigo-700 hover:text-indigo-800 hover:underline"
            >
              {t('View terms and conditions')}
            </button>
          )}

          {/* BES-7708: cost breakdown, revealed on selection (GuestyPay hosts).
              Mirrors the upsell price breakdown so the guest sees the tax-inclusive
              total before paying. */}
          {isSelected && taxBreakdown && (
            <div className="mt-4 space-y-1.5 rounded-xl bg-white/70 p-3 text-sm">
              <div className="flex justify-between text-zinc-600">
                <span>{taxBreakdown.subtotalLabel}</span>
                <span>{taxBreakdown.subtotalAmount}</span>
              </div>
              <div className="flex justify-between text-zinc-600">
                <span>{taxBreakdown.taxLabel}</span>
                <span>{taxBreakdown.taxAmount}</span>
              </div>
              <div className="flex justify-between border-t border-zinc-200 pt-1.5 font-semibold text-zinc-900">
                <span>{taxBreakdown.totalLabel}</span>
                <span>{taxBreakdown.totalAmount}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DamageWaiver() {
  const navigate = useNavigate();
  const { t } = useTranslation('damageWaiver');
  const location = useLocation();
  const portalOutlet = useOutletContext<PortalOutletContext | null>();
  const refetchPortal = portalOutlet?.refetch;
  const inPortal = location.pathname.startsWith('/portal/');
  const homePath = inPortal ? '/portal/home' : '/';
  const resultPath = inPortal ? '/portal/home/damage-waiver-result' : '/damage-waiver-result';
  const returnPath = useMemo(
    () => getSafeReturnPath(new URLSearchParams(location.search).get('return')),
    [location.search]
  );
  const backPath = returnPath || homePath;
  const { language, setLanguage, isLanguageFromStorage } = useLanguage();
  const isLanguageFromStorageRef = useRef(isLanguageFromStorage);
  useEffect(() => {
    isLanguageFromStorageRef.current = isLanguageFromStorage;
  }, [isLanguageFromStorage]);
  const [data, setData] = useState<DamageWaiverData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const chargeErrorCopy = useChargeErrorCopy();
  const [selectedOption, setSelectedOption] = useState<ProtectionOption | null>(null);

  // Clear selection if the chosen option is no longer visible (e.g. waiver hidden after re-fetch)
  useEffect(() => {
    if (!data) return;
    if (selectedOption === 'waiver' && data.waiverCost <= 0) {
      setSelectedOption(null);
    }
  }, [data, selectedOption]);

  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showFailure, setShowFailure] = useState(false);
  // BES-9535: the failure CODE, not just its copy. The screen below needs it to decide whether
  // offering Try Again would post a second folio line.
  const [failureCode, setFailureCode] = useState<string | null>(null);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsModalContent, setTermsModalContent] = useState<{ title: string; content: string }>({
    title: '',
    content: '',
  });
  const [retryCount, setRetryCount] = useState(0);
  const [cardModalState, setCardModalState] = useState<{
    paymentProviderId: string;
    listingId?: string | null;
    pmsReservationId?: string | null;
    // BES-7708: `amount` is the tax-inclusive total disclosed to the card form
    // (matches the option card + the upsell modal). `chargeAmount` is the pre-tax
    // base we actually post to the GuestyPay charge - Guesty re-taxes the folio,
    // so inflating the charge to the inclusive total would double-tax.
    amount: number;
    chargeAmount: number;
    currency: string;
    option: 'waiver' | 'hold';
  } | null>(null);
  const [ccAuthPending, setCcAuthPending] = useState<{
    authType: 'charge' | 'deposit';
    amount: number;
    currency: string;
    context: Record<string, unknown>;
    run: () => Promise<void>;
  } | null>(null);

  // set title
  useEffect(() => {
    document.title = t('Secure your trip');
  }, [t]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      let willRefetch = false;
      try {
        setLoading(true);
        setError(null);
        setFailureCode(null);
        const response: DamageWaiverData | CompletedWaiverData = await apiClient.get(
          `/damageWaiver?language=${language}`
        );

        if (cancelled) return;

        // Check if damage waiver is already completed
        if (response.isCompleted) {
          const completedData = response as CompletedWaiverData;
          navigate(resultPath, {
            state: {
              status: 'success',
              message: completedData.type
                ? `Your ${completedData.type === 'waiver' ? 'damage waiver' : completedData.type === 'hold' ? 'security deposit' : 'protection option'} has already been processed.`
                : t('Your damage protection has already been completed.'),
              type: completedData.type,
              amount: completedData.amount != null ? completedData.amount.toString() : null,
              currency: completedData.currency,
              isFromCompleted: true,
              returnPath,
            },
          });
          return;
        }

        const waiverData = response as DamageWaiverData;

        // Initialize language from host's default if not set by user
        if (
          waiverData.defaultLanguage &&
          !isLanguageFromStorageRef.current &&
          waiverData.defaultLanguage !== language
        ) {
          willRefetch = true;
          setLanguage(waiverData.defaultLanguage);
          return;
        }

        setData(waiverData);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : t('Failed to load damage waiver options'));
      } finally {
        if (!cancelled && !willRefetch) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [language, retryCount]);

  const showTerms = (type: 'waiver' | 'hold') => {
    if (!data?.terms) return;

    const content = type === 'waiver' ? data.terms.waivers : data.terms.holds;
    if (!content) return;

    setTermsModalContent({
      title: type === 'waiver' ? t('Damage Waiver Terms & Conditions') : t('Security Deposit Terms & Conditions'),
      content: content,
    });
    setShowTermsModal(true);
  };

  // Runs the actual waiver/hold submission. Split out so the BES-7448/7449
  // credit-card authorization step can run before it for gated hosts.
  const proceedWaiverSubmit = async (requestBody: DamageWaiverRequest, option: 'waiver' | 'hold') => {
    if (!data) return;
    try {
      setSubmitting(true);
      setError(null);
      setFailureCode(null);

      const response: CheckoutResponse = await apiClient.post('/damageWaiver', requestBody);

      if (response.completed) {
        setShowSuccess(true);
        void refetchPortal?.();
        return;
      }

      if (response.requiresCardInput) {
        if (!response.paymentProviderId) {
          setError(t('Unable to process payment. Please try again later.'));
          setShowFailure(true);
          return;
        }
        const base = requestBody.amount ?? 0;
        const waiverTax = option === 'waiver' ? data.waiverTax ?? 0 : 0;
        setCardModalState({
          paymentProviderId: response.paymentProviderId,
          listingId: response.listingId,
          pmsReservationId: response.pmsReservationId,
          amount: base + waiverTax, // disclosed in the card form (tax-inclusive)
          chargeAmount: base, // pre-tax base posted to the charge (Guesty re-taxes)
          currency: data.currency,
          option,
        });
        return;
      }

      if (response.checkoutUrl) {
        try {
          window.location.assign(response.checkoutUrl);
        } catch (redirectError) {
          window.open(response.checkoutUrl, '_self');
        }
      } else {
        setShowSuccess(true);
        void refetchPortal?.();
      }
    } catch (err) {
      const code = (err as { code?: string } | undefined)?.code;
      setFailureCode(code ?? null);
      setError(chargeErrorCopy(code, err instanceof Error ? err.message : undefined));
      setShowFailure(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedOption || !data) return;

    const requestBody: DamageWaiverRequest = {
      option: selectedOption,
      amount: selectedOption === 'waiver' ? data.waiverCost : selectedOption === 'hold' ? data.holdAmount : null,
      currency: data.currency,
      configId: data.configId,
      returnPath,
    };

    if (selectedOption === 'none') {
      try {
        setSubmitting(true);
        setError(null);
        setFailureCode(null);
        await apiClient.post('/damageWaiver', requestBody);
        setShowSuccess(true);
        void refetchPortal?.();
      } catch (err) {
        const code = (err as { code?: string } | undefined)?.code;
        setFailureCode(code ?? null);
        setError(chargeErrorCopy(code, err instanceof Error ? err.message : undefined));
        setShowFailure(true);
      } finally {
        setSubmitting(false);
      }
      return;
    }

    const option = selectedOption as 'waiver' | 'hold';

    // BES-7448 / BES-7449: gated hosts must e-sign a credit-card authorization
    // before the payment screen. The hold (security deposit) uses the deposit
    // copy; the waiver fee uses the charge copy.
    const baseAmount = requestBody.amount ?? 0;
    if (data.ccAuthRequired && baseAmount > 0) {
      // BES-7708: authorize the tax-inclusive total the card will actually be
      // charged (GuestyPay re-taxes the waiver fee on the folio). The charge call
      // still posts the pre-tax base via requestBody.amount, so this only changes
      // the disclosed authorization amount, not what we send to charge.
      const authAmount =
        option === 'waiver' && data.waiverTax !== undefined ? baseAmount + data.waiverTax : baseAmount;
      setCcAuthPending({
        authType: option === 'hold' ? 'deposit' : 'charge',
        amount: authAmount,
        currency: data.currency,
        context: { kind: 'damage_waiver', option, configId: data.configId },
        run: () => proceedWaiverSubmit(requestBody, option),
      });
      return;
    }

    await proceedWaiverSubmit(requestBody, option);
  };

  const handleGuestyPayTokenized = useCallback(
    async (tokenId: string) => {
      if (!cardModalState || !data) return;
      const result: { completed?: boolean } = await apiClient.post('/damageWaiver/purchase-with-token', {
        option: cardModalState.option,
        // BES-7708: charge the pre-tax base; the backend posts it to the folio and
        // Guesty applies the waiver tax. The card form disclosed the inclusive total.
        amount: cardModalState.chargeAmount,
        currency: cardModalState.currency,
        configId: data.configId,
        tokenId,
        paymentProviderId: cardModalState.paymentProviderId,
      });
      if (result.completed) {
        setCardModalState(null);
        setShowSuccess(true);
        void refetchPortal?.();
      } else {
        throw new Error('Payment failed');
      }
    },
    [cardModalState, data, refetchPortal]
  );

  // Handle return from Stripe checkout
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const canceled = urlParams.get('canceled');

    if (success === 'true') {
      setShowSuccess(true);
      void refetchPortal?.();
    } else if (canceled === 'true') {
      setShowFailure(true);
    }
  }, [refetchPortal]);

  if (loading) {
    return (
      <MobileView>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">{t('Loading damage protection options...')}</p>
          </div>
        </div>
      </MobileView>
    );
  }

  if (showSuccess) {
    return (
      <MobileView>
        <div className="flex items-center justify-center min-h-screen p-8">
          <div className="text-center space-y-6">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('Success!')}</h2>
              <p className="text-gray-600">{t('Your damage protection selection has been processed successfully.')}</p>
            </div>
            <Button onClick={() => navigate(backPath)} className="w-full">
              {t('Continue')}
            </Button>
          </div>
        </div>
      </MobileView>
    );
  }

  if (showFailure) {
    return (
      <MobileView>
        <div className="flex items-center justify-center min-h-screen p-8">
          <div className="text-center space-y-6">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('Payment Failed')}</h2>
              {/* BES-9535: show the mapped reason. This screen used to render a fixed
                  "please try again" line, which discarded the copy the catch handler had
                  just built and told the guest to retry even when a retry is unsafe. */}
              <p className="text-gray-600">
                {error || t('There was an issue processing your payment. Please try again or contact support.')}
              </p>
            </div>
            <div className="space-y-3">
              {/* BES-9535: a retry on these codes posts a SECOND live folio line, because the
                  card-on-file path charges without mounting the modal that would have blocked
                  Pay. Go Back is the only safe action; the copy above says to message the host. */}
              {!(failureCode && RETRY_LEAVES_LIVE_LINE.has(failureCode)) && (
                <Button onClick={() => setShowFailure(false)} className="w-full">
                  {t('Try Again')}
                </Button>
              )}
              <Button variant="outline" onClick={() => navigate(backPath)} className="w-full">
                {t('Go Back')}
              </Button>
            </div>
          </div>
        </div>
      </MobileView>
    );
  }

  if (error && !data) {
    return (
      <MobileView>
        <div className="flex items-center justify-center min-h-screen p-8">
          <div className="text-center space-y-6">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">{t('Error Loading Options')}</h2>
              <p className="text-gray-600 text-sm">{error}</p>
            </div>
            <Button onClick={() => setRetryCount((c) => c + 1)} className="w-full">
              {t('Retry')}
            </Button>
          </div>
        </div>
      </MobileView>
    );
  }

  return (
    <MobileView>
      <div className={`px-4 py-12 ${showTermsModal ? 'blur-sm pointer-events-none' : ''}`}>
        <div className="flex items-center justify-between gap-3 mb-2">
          {returnPath ? (
            <Button variant="outline" onClick={() => navigate(returnPath)} className="text-xs px-3 py-1.5">
              {t('Go Back')}
            </Button>
          ) : (
            <div />
          )}
          <LanguageSelector />
        </div>

        <div className="space-y-6">
          {data && data.waiverCost > 0 && (
            <ProtectionOptionCard
              value="waiver"
              selected={selectedOption || ''}
              onChange={(value) => setSelectedOption(value as ProtectionOption)}
              title={t('Damage waiver')}
              price={`${formatCurrency(data.waiverCost, data.currency)}`}
              priceCaption={t('one-time fee')}
              taxBreakdown={
                data.waiverTax !== undefined
                  ? {
                      subtotalLabel: t('Subtotal'),
                      subtotalAmount: formatCurrency(data.waiverCost, data.currency),
                      taxLabel: t('Estimated tax'),
                      taxAmount: formatCurrency(data.waiverTax, data.currency),
                      totalLabel: t('Total'),
                      totalAmount: formatCurrency(data.waiverCost + data.waiverTax, data.currency),
                    }
                  : undefined
              }
              bullets={[
                t('Instantly covers accidental damages up to {{amount}}', {
                  amount: data?.holdAmount
                    ? formatCurrency(data.holdAmount, data?.currency || 'USD')
                    : 'the specified amount',
                }),
                t('Non-refundable, no deposit required'),
                t('Best for peace of mind'),
              ]}
              tag={t('Most Popular')}
              termsContent={data?.terms?.waivers || undefined}
              onShowTerms={() => showTerms('waiver')}
            />
          )}

          {data?.holdAmount && (
            <ProtectionOptionCard
              value="hold"
              selected={selectedOption || ''}
              onChange={(value) => setSelectedOption(value as ProtectionOption)}
              title={t('Security Deposit')}
              price={data ? `${formatCurrency(data.holdAmount, data.currency)}` : '$500'}
              priceCaption={t('fully refundable')}
              bullets={[
                t('Held until {{days}} days after checkout (subject to bank processing times)', {
                  days: data.holdLengthDays,
                }),
                t('If damages occur, you will get charged'),
                t('Will place a hold on your card'),
              ]}
              termsContent={data?.terms?.holds || undefined}
              onShowTerms={() => showTerms('hold')}
            />
          )}

          {!data?.holdAmount && (
            <ProtectionOptionCard
              value="none"
              selected={selectedOption || ''}
              onChange={(value) => setSelectedOption(value as ProtectionOption)}
              title={t('Skip this option')}
              bullets={[t("Not recommended – you'll be responsible for any damages")]}
            />
          )}
        </div>

        {/* Help box */}
        <div className="mt-8 rounded-lg border border-zinc-200 bg-white px-4 py-3 text-sm text-zinc-600">
          <div className="flex items-center gap-2">
            <svg className="h-4 w-4 text-zinc-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
              <path
                fillRule="evenodd"
                d="M18 10A8 8 0 11.001 10 8 8 0 0118 10zM9 7a1 1 0 112 0 1 1 0 01-2 0zm2 8H9v-1a3 3 0 013-3h.25a.75.75 0 000-1.5H12a4.5 4.5 0 00-4.5 4.5V15h5.5a.5.5 0 010 1z"
                clipRule="evenodd"
              />
            </svg>
            <span>{t('Contact us if you have any questions.')}</span>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <svg className="w-5 h-5 text-red-400 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Confirm Button */}
        {selectedOption && (
          <div className="sticky bottom-0 bg-white pt-6 pb-4 -mx-4 px-4">
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full rounded-full h-14 text-base"
              variant="primary"
              size="lg"
            >
              {submitting ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  {t('Processing...')}
                </div>
              ) : (
                t('Confirm')
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Credit-card authorization e-sign (BES-7448 / BES-7449) */}
      {ccAuthPending && (
        <CcAuthModal
          authType={ccAuthPending.authType}
          amount={ccAuthPending.amount}
          currency={ccAuthPending.currency}
          context={ccAuthPending.context}
          onAuthorized={() => {
            const pending = ccAuthPending;
            setCcAuthPending(null);
            void pending.run();
          }}
          onCancel={() => setCcAuthPending(null)}
        />
      )}

      {/* Guesty Pay Card Modal */}
      {cardModalState && (
        <GuestyPayCardModal
          paymentProviderId={cardModalState.paymentProviderId}
          listingId={cardModalState.listingId}
          reservationId={cardModalState.pmsReservationId}
          productKey={`damage_waiver:${cardModalState.option}`}
          amount={cardModalState.amount}
          currency={cardModalState.currency}
          title={cardModalState.option === 'hold' ? t('Save Card Details') : undefined}
          subtitle={
            cardModalState.option === 'hold' ? t('Your card will be saved for the security deposit.') : undefined
          }
          submitLabel={cardModalState.option === 'hold' ? t('Save Card') : t('Pay')}
          onTokenized={handleGuestyPayTokenized}
          onCancel={() => setCardModalState(null)}
        />
      )}

      {/* Terms Modal */}
      <TermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        title={termsModalContent.title}
        content={termsModalContent.content}
      />
    </MobileView>
  );
}

export default DamageWaiver;
